import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { KTSVG } from '../../../_metronic/helpers'
import {
  getComments,
  getCommentStats,
  markCommentHandled,
  syncComments,
  FacebookComment
} from './core/_requests'

const API_URL = process.env.REACT_APP_API_URL || ''

interface Profile {
  _id: string
  facebook_username?: string
  facebook_id?: string
  first_name?: string
  last_name?: string
  is_logged_in?: boolean
  parent_profile?: string
}

interface Props {
  onCommentClick?: (comment: FacebookComment) => void
}

interface Stats {
  total: number
  pending: number
  handled: number
  all_total?: number
  all_pending?: number
  all_handled?: number
  bot_posts_total?: number
  external_posts_total?: number
  scanned?: number
  truncated?: boolean
}

const FacebookCommentsSection: React.FC<Props> = ({ onCommentClick }) => {
  const [comments, setComments] = useState<FacebookComment[]>([])
  const [stats, setStats] = useState<Stats | null>(null)
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [syncMessage, setSyncMessage] = useState<string | null>(null)
  const [filter, setFilter] = useState<'all' | 'pending' | 'handled'>('all')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [profiles, setProfiles] = useState<Profile[]>([])
  const [selectedProfile, setSelectedProfile] = useState<string>('all')
  const [showAllComments, setShowAllComments] = useState(true)

  // Reply status (replied / pending / no-reply-sent) is admin-only. An admin
  // keeps `admin_auth_token` in localStorage even while impersonating a client
  // account (auto-login does not clear it), whereas a real client never has it.
  const isAdmin = !!localStorage.getItem('admin_auth_token')

  useEffect(() => {
    loadProfiles()
  }, [])

  useEffect(() => {
    loadData()
  }, [filter, page, selectedProfile, showAllComments])

  const loadProfiles = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/get-single-profile`)
      // Show ALL profiles in the filter (parents AND children). Notifications
      // are stored per-actor, so a user with comments on a sub-profile needs
      // to be able to pin the filter to that sub-profile. Parents are sorted
      // first; children appear under them in load order.
      const all: Profile[] = response.data.all_users || []
      const parentIdSet = new Set(all.filter(p => !p.parent_profile).map(p => p._id))
      const sorted = [
        ...all.filter(p => !p.parent_profile),                                    // parents
        ...all.filter(p => p.parent_profile && parentIdSet.has(p.parent_profile)) // their children
      ]
      setProfiles(sorted)
    } catch (err) {
      console.error('Failed to load profiles:', err)
    }
  }

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)

      const params: any = { page, limit: 15 }
      if (filter === 'pending') params.handled = false
      if (filter === 'handled') params.handled = true
      if (selectedProfile !== 'all') params.profile_id = selectedProfile
      if (showAllComments) params.bot_posts_only = false

      const [commentsRes, statsRes] = await Promise.all([
        getComments(params),
        getCommentStats({ bot_posts_only: !showAllComments })
      ])

      setComments(commentsRes.comments)
      setTotalPages(commentsRes.pagination.pages)
      setStats(statsRes.stats)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load comments')
    } finally {
      setLoading(false)
    }
  }

  const handleSync = async () => {
    try {
      setSyncing(true)
      setSyncMessage(null)
      setError(null)

      // Sync selected profile or all profiles
      const profileId = selectedProfile !== 'all' ? selectedProfile : undefined
      const result = await syncComments(profileId)
      setSyncMessage(result.message || 'סנכרון הופעל בהצלחה')

      // Reload data after a short delay to allow sync to process
      setTimeout(() => {
        loadData()
        setSyncMessage(null)
      }, 5000)
    } catch (err: any) {
      setError(err.response?.data?.error || 'שגיאה בהפעלת הסנכרון')
    } finally {
      setSyncing(false)
    }
  }

  const handleMarkHandled = async (id: string, handled: boolean) => {
    try {
      await markCommentHandled(id, handled)
      loadData()
    } catch (err) {
      console.error('Failed to update comment:', err)
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('he-IL', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className="card mb-8">
      <div className="card-header border-0 pt-5">
        <h3 className="card-title align-items-start flex-column">
          <span className="card-label fw-bold fs-3 mb-1">תגובות על פוסטים בקבוצות</span>
          <span className="text-muted fw-semibold fs-7">
            {isAdmin && showAllComments ? 'כל התגובות מקבוצות פייסבוק' : 'תגובות מקבוצות פייסבוק על פוסטים שפורסמו מהמערכת'}
          </span>
        </h3>
        <div className="card-toolbar gap-2">
          {/* Show All Toggle (admin only — clients see all comments framed as our posts) */}
          {isAdmin && (
            <div className="form-check form-switch form-check-custom form-check-solid me-3">
              <input
                className="form-check-input"
                type="checkbox"
                id="showAllComments"
                checked={showAllComments}
                onChange={(e) => { setShowAllComments(e.target.checked); setPage(1) }}
              />
              <label className="form-check-label fw-semibold text-muted fs-7" htmlFor="showAllComments">
                הצג כל התגובות
              </label>
            </div>
          )}
          {/* Profile Selector */}
          <select
            className="form-select form-select-sm w-250px"
            value={selectedProfile}
            onChange={(e) => { setSelectedProfile(e.target.value); setPage(1) }}
          >
            <option value="all">כל הפרופילים</option>
            {profiles.map((profile) => {
              const isChild = !!profile.parent_profile
              const label = profile.first_name || profile.facebook_username || profile._id
              return (
                <option key={profile._id} value={profile._id}>
                  {isChild ? '↳ ' : ''}{label}
                  {profile.is_logged_in ? ' ✓' : ' (לא מחובר)'}
                </option>
              )
            })}
          </select>
          <button
            className="btn btn-sm btn-light-success"
            onClick={handleSync}
            disabled={syncing}
          >
            {syncing ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                מסנכרן...
              </>
            ) : (
              <>
                <KTSVG path='/media/icons/duotune/arrows/arr027.svg' className='svg-icon-3 me-1' />
                סנכרן מפייסבוק
              </>
            )}
          </button>
          <button
            className="btn btn-sm btn-light-primary"
            onClick={() => loadData()}
            disabled={loading}
          >
            <KTSVG path='/media/icons/duotune/arrows/arr029.svg' className='svg-icon-3 me-1' />
            רענן
          </button>
        </div>
      </div>

      <div className="card-body py-3">
        {/* Sync Message */}
        {syncMessage && (
          <div className="alert alert-success d-flex align-items-center mb-4">
            <KTSVG path='/media/icons/duotune/general/gen043.svg' className='svg-icon-2 text-success me-3' />
            {syncMessage}
          </div>
        )}

        {/* Stats */}
        {stats && (
          <div className="d-flex gap-4 mb-5 flex-wrap">
            <div className="border border-gray-300 border-dashed rounded py-3 px-4">
              <div className="fs-4 fw-bold text-gray-700">{stats.total}</div>
              <div className="fw-semibold text-muted">סה״כ</div>
            </div>
            {isAdmin && (
              <>
                <div className="border border-warning border-dashed rounded py-3 px-4">
                  <div className="fs-4 fw-bold text-warning">{stats.pending}</div>
                  <div className="fw-semibold text-muted">ממתינות לתגובה</div>
                </div>
                <div className="border border-success border-dashed rounded py-3 px-4">
                  <div className="fs-4 fw-bold text-success">{stats.handled}</div>
                  <div className="fw-semibold text-muted">הגיבו</div>
                </div>
              </>
            )}
            {stats.truncated && (
              <div className="d-flex align-items-center text-muted fs-7">
                <i className="bi bi-info-circle me-1"></i>
                מציג את {stats.scanned?.toLocaleString()} התגובות האחרונות
              </div>
            )}
          </div>
        )}

        {/* Filter */}
        <div className="d-flex gap-2 mb-5">
          <button
            className={`btn btn-sm ${filter === 'all' ? 'btn-primary' : 'btn-light'}`}
            onClick={() => { setFilter('all'); setPage(1) }}
          >
            הכל
          </button>
          {isAdmin && (
            <>
              <button
                className={`btn btn-sm ${filter === 'pending' ? 'btn-warning' : 'btn-light'}`}
                onClick={() => { setFilter('pending'); setPage(1) }}
              >
                ממתינות ({stats?.pending || 0})
              </button>
              <button
                className={`btn btn-sm ${filter === 'handled' ? 'btn-success' : 'btn-light'}`}
                onClick={() => { setFilter('handled'); setPage(1) }}
              >
                הגיבו ({stats?.handled || 0})
              </button>
            </>
          )}
        </div>

        {error && (
          <div className="alert alert-danger d-flex align-items-center mb-4">
            <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2 text-danger me-3' />
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-10">
            <span className="spinner-border spinner-border-lg text-primary"></span>
            <p className="text-muted mt-3">טוען תגובות...</p>
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-10 text-muted">
            <KTSVG path='/media/icons/duotune/communication/com012.svg' className='svg-icon-3x mb-3' />
            <p>לא נמצאו תגובות על פוסטים שפורסמו מהמערכת</p>
            <p className="fs-7">לחץ על "סנכרן מפייסבוק" כדי לטעון תגובות חדשות</p>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
                <thead>
                  <tr className="fw-bold text-muted">
                    <th className="min-w-200px">תגובה</th>
                    <th className="min-w-100px">מאת</th>
                    {isAdmin && showAllComments && <th className="min-w-120px">מזהה פוסט</th>}
                    <th className="min-w-100px">תאריך</th>
                    {isAdmin && <th className="min-w-100px">סטטוס</th>}
                    <th className="min-w-100px text-end">פעולות</th>
                  </tr>
                </thead>
                <tbody>
                  {comments.map((comment) => (
                    <tr key={comment._id}>
                      <td>
                        <div className="d-flex align-items-center">
                          {comment.icon_url && (
                            <div className="symbol symbol-40px me-3">
                              <img src={comment.icon_url} alt="" />
                            </div>
                          )}
                          <div className="d-flex flex-column">
                            <a
                              href={comment.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-dark fw-bold text-hover-primary fs-6 cursor-pointer"
                              style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
                              title={comment.message || 'ללא תוכן'}
                            >
                              {comment.message || 'ללא תוכן'}
                            </a>
                            {comment.url && (
                              <span className="text-muted fw-semibold fs-8">
                                לחץ לפתיחה בפייסבוק
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        {comment.actors && comment.actors.length > 0 ? (
                          <div className="d-flex align-items-center">
                            {comment.actors[0].profile_picture && (
                              <div className="symbol symbol-30px me-2">
                                <img src={comment.actors[0].profile_picture} alt="" className="rounded-circle" />
                              </div>
                            )}
                            {comment.actors[0].profile_url ? (
                              <a
                                href={comment.actors[0].profile_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-700 fs-7 text-hover-primary"
                              >
                                {comment.actors[0].name}
                              </a>
                            ) : (
                              <span className="text-gray-700 fs-7">{comment.actors[0].name}</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      {isAdmin && showAllComments && (
                        <td>
                          {comment.post_id ? (
                            <a
                              href={`https://www.facebook.com/${comment.post_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary fs-8 text-hover-dark"
                              title={`Post ID: ${comment.post_id}`}
                            >
                              {comment.post_id.length > 15 ? `...${comment.post_id.slice(-12)}` : comment.post_id}
                            </a>
                          ) : (
                            <span className="text-muted fs-8">-</span>
                          )}
                        </td>
                      )}
                      <td>
                        <span className="text-muted fs-7">
                          {formatDate(comment.timestamp || comment.created_at)}
                        </span>
                      </td>
                      {isAdmin && (
                        <td>
                          {comment.handled ? (
                            <span className="badge badge-light-success">הגיבו</span>
                          ) : showAllComments && comment.is_bot_post === false ? (
                            <div className="d-flex flex-column">
                              <span className="badge badge-light-secondary mb-1">לא פורסם מהמערכת</span>
                              <span className="text-muted fs-8">לא נשלחה תגובה</span>
                            </div>
                          ) : (
                            <span className="badge badge-light-warning">ממתין</span>
                          )}
                        </td>
                      )}
                      <td className="text-end">
                        {comment.url && (
                          <a
                            href={comment.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1"
                            title="פתח בפייסבוק"
                          >
                            <KTSVG path='/media/icons/duotune/arrows/arr095.svg' className='svg-icon-3' />
                          </a>
                        )}
                        {isAdmin && (comment.handled ? (
                          <button
                            className="btn btn-icon btn-bg-light btn-active-color-warning btn-sm"
                            onClick={() => handleMarkHandled(comment._id, false)}
                            title="סמן כלא טופל"
                          >
                            <KTSVG path='/media/icons/duotune/arrows/arr029.svg' className='svg-icon-3' />
                          </button>
                        ) : (
                          <button
                            className="btn btn-icon btn-bg-light btn-active-color-success btn-sm"
                            onClick={() => handleMarkHandled(comment._id, true)}
                            title="סמן כטופל"
                          >
                            <KTSVG path='/media/icons/duotune/arrows/arr085.svg' className='svg-icon-3' />
                          </button>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-center mt-5">
                <button
                  className="btn btn-sm btn-light me-2"
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  <i className="bi bi-chevron-right"></i>
                </button>
                <span className="align-self-center text-muted">
                  עמוד {page} מתוך {totalPages}
                </span>
                <button
                  className="btn btn-sm btn-light ms-2"
                  disabled={page === totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  <i className="bi bi-chevron-left"></i>
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default FacebookCommentsSection
